import { Button } from 'components/buttons/button';
import {
  inputClassNames,
  labelClassNames,
  StandardInput,
  ValidatedInput,
} from 'components/forms/input';
import { Layout } from 'components/layout';
import { LogoIcon } from 'components/svg/logo-icon';
import { useBasket } from 'lib/hooks/basket/use-basket';
import { useInput } from 'lib/hooks/use-input';
import { emailRegex } from 'lib/utils';
import { useState } from 'react';
import currency from 'currency.js';
import { GetServerSideProps } from 'next';
import { addApolloState, initializeApollo } from 'lib/hooks/apollo-client';
import {
  GetUnstructuredDataDocument,
  GetUnstructuredDataQueryVariables,
  useGetUnstructuredDataQuery,
} from 'lib/graphql-generated';
import { RenderContent } from 'components/content-renderer';

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const client = initializeApollo();
  await client.query({
    query: GetUnstructuredDataDocument,
    variables: { title: 'vouchers' } as GetUnstructuredDataQueryVariables,
  });

  return addApolloState(client, { props: {} });
};

export const VoucherPage = () => {
  const amount = useInput<number>(5);
  const recipient = useInput<string>('');
  const [submitted, setSubmitted] = useState<boolean>(false);
  const note = useInput<string>('');
  const [basket, modifyBasket] = useBasket();
  const { data, loading } = useGetUnstructuredDataQuery({
    variables: { title: 'vouchers' },
  });
  return (
    <Layout pageName='Voucher'>
      <article className='[ order-1 grid grid-cols-1 gap-y-6 py-16 ] [ md:grid-cols-5 md:gap-y-8 md:max-w-screen-md md:px-4 md:pb-8 ] [ lg:max-w-screen-xl lg:grid-cols-7 lg:gap-x-8 lg:order-3 ] w-full justify-items-center mx-auto'>
        <img
          alt={`Image for lighthouse vouchers`}
          className='max-w-2xs sm:max-w-xs [ md:max-w-full md:col-span-2 ] [ ]'
          src={data.unstructured_data_by_pk.data.image}
        />
        <section className='prose px-8 md:col-span-5 md:col-start-1 justify-self-start md:px-0  [ lg:col-start-3 lg:col-span-3 lg:row-start-1 ] '>
          <RenderContent content={data.unstructured_data_by_pk.data.content} />
        </section>
        <section className='[ max-w-2xs sm:max-w-xs flex flex-col ] [ md:mb-auto md:flex-col md:col-span-3 ] [ lg:col-span-2 lg:col-start-6 ] flex-wrap '>
          <h1 className='max-w-2xs sm:max-w-xs'>
            <span className='bg-green-default text-white font-header tracking-wide uppercase pt-1 px-1 whitespace-pre-wrap text-2xl'>
              Lighthouse Bookshop Gift Voucher
            </span>
          </h1>

          <div className='my-auto uppercase font-header tracking-wide text-2xl md:mt-4 space-y-4 flex flex-col'>
            <ValidatedInput
              errorMessage={
                !emailRegex.test(recipient.value) &&
                recipient.value.trim() !== ''
                  ? 'Please enter a valid email address'
                  : ''
              }
              showError={
                !emailRegex.test(recipient.value) &&
                recipient.value.trim() !== ''
              }
              label='Recipients Email'
              id='recipient'
              name='recipient'
              bind={{ ...recipient.bind }}
              type='email'
            />
            <ValidatedInput
              errorMessage={
                amount.value < 5 ? 'Amount must be more than £5' : ''
              }
              showError={amount.value < 5}
              label='Voucher Value (£)'
              id='amount'
              name='amount'
              inputProps={{ min: 5 }}
              bind={{
                value: amount.value,
                onChange: (e) =>
                  amount.setValue(Math.round(parseInt(e.target.value))),
              }}
              type='number'
            />
            <label className={labelClassNames}>
              Message
              <p className='italic text-xs normal-case font-normal font-sans'>
                Send the recipient of this voucher a little message!
              </p>
              <textarea
                className={inputClassNames}
                value={note.value}
                onChange={(e) => note.setValue(e.target.value)}
              />
            </label>
            <Button
              className='md:mt-4 ml-auto my-auto md:ml-0'
              buttonType='primary'
              buttonSize='md'
              disabled={amount.value < 5 || !emailRegex.test(recipient.value)}
              onClick={() => {
                modifyBasket({
                  type: 'add',
                  payload: {
                    type: 'voucher',
                    id: `voucher_${
                      basket.items.filter((item) => item.type === 'voucher')
                        .length
                    }`,
                    data: {
                      price: currency(amount.value).intValue,
                      discount: 0,
                      quantity: 1,
                      voucher: {
                        image: data.unstructured_data_by_pk.data.image,
                        recipient: recipient.value,
                        message: note.value,
                      },
                    },
                  },
                });
                recipient.init('');
                setSubmitted(true);
              }}>
              Add To Basket
            </Button>
          </div>
        </section>
      </article>
    </Layout>
  );
};

export default VoucherPage;
